import React from "react";
import usePlayer from "../hooks/usePlayer";
import spotify from "../assets/images/spotify.png";
import { ReactComponent as Battery0 } from "../assets/svgs/sf-symbols/battery/battery.0percent.svg";
import { ReactComponent as Battery25 } from "../assets/svgs/sf-symbols/battery/battery.25percent.svg";
import { ReactComponent as Battery50 } from "../assets/svgs/sf-symbols/battery/battery.50percent.svg";
import { ReactComponent as Battery75 } from "../assets/svgs/sf-symbols/battery/battery.75percent.svg";
import { ReactComponent as Battery100 } from "../assets/svgs/sf-symbols/battery/battery.100percent.svg";
import { ReactComponent as BatteryCharging } from "../assets/svgs/sf-symbols/battery/battery.100percent.bolt.svg";
import { ReactComponent as EnergyAutomatic } from "../assets/svgs/sf-symbols/battery/energy-automatic.svg";
import { ReactComponent as EnergyLowPower } from "../assets/svgs/sf-symbols/battery/energy-low-power.svg";
import { ReactComponent as EnergyHighPower } from "../assets/svgs/sf-symbols/battery/energy-high-power.svg";

const ENERGY_MODES = [
  { id: "automatic", label: "Automatic", Icon: EnergyAutomatic },
  { id: "low", label: "Low Power", Icon: EnergyLowPower },
  { id: "high", label: "High Power", Icon: EnergyHighPower },
];

// Menu bar battery: the glyph closest to the charge level, a bolt while charging, and a yellow
// fill in Low Power Mode, as macOS shows it.
export const BatteryGlyph = ({ battery, lowPower, className = "" }) => {
  const { level, charging } = battery;
  const Glyph = charging
    ? BatteryCharging
    : level >= 0.88
    ? Battery100
    : level >= 0.63
    ? Battery75
    : level >= 0.38
    ? Battery50
    : level >= 0.13
    ? Battery25
    : Battery0;
  return <Glyph className={`battery-glyph ${lowPower ? "is-low-power" : ""} ${className}`} />;
};

// 1:05
const formatHours = (seconds) => {
  const m = Math.round(seconds / 60);
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;
};

const chargingStatus = ({ level, charging, chargingTime }) => {
  if (!charging) return null;
  if (level >= 1 || chargingTime === 0) return "Fully Charged";
  if (Number.isFinite(chargingTime)) return `${formatHours(chargingTime)} until full`;
  return "Charging";
};

// The Battery menu from the menu bar, laid out like macOS Tahoe's.
const BatteryMenu = ({ battery, energyMode, onEnergyModeChange, onOpenSettings }) => {
  const { playing } = usePlayer();
  const status = chargingStatus(battery);

  return (
    <div className="menu-body" role="menu" aria-label="Battery">
      <div className="menu-head">
        <span>Battery</span>
        <span className="menu-value">{Math.round(battery.level * 100)}%</span>
      </div>
      <p className="menu-info">Power Source: {battery.charging ? "Power Adapter" : "Battery"}</p>
      {status && <p className="menu-info">{status}</p>}

      <hr className="menu-sep" />
      <p className="menu-section">Energy Mode</p>
      <div role="group" aria-label="Energy Mode">
        {ENERGY_MODES.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            role="menuitemradio"
            aria-checked={energyMode === id}
            onClick={() => onEnergyModeChange(id)}
            className="menu-option"
          >
            <span className={`menu-option-icon ${energyMode === id ? "is-selected" : ""}`}>
              <Icon />
            </span>
            {label}
          </button>
        ))}
      </div>

      <hr className="menu-sep" />
      {playing ? (
        <>
          <p className="menu-section">Using Significant Energy</p>
          <p className="menu-app">
            <img src={spotify} alt="" />
            Spotify
          </p>
        </>
      ) : (
        <p className="menu-info">No Apps Using Significant Energy</p>
      )}

      <hr className="menu-sep" />
      <button type="button" role="menuitem" onClick={onOpenSettings} className="menu-item">
        Battery Settings…
      </button>
    </div>
  );
};

export default BatteryMenu;
